import React, { useCallback, useEffect, useRef, useState } from 'react'
import useHeaderOffset from './useHeaderOffset'
import useSidebarGate from './useSidebarGate'
import './SidebarSlider.component.scss'

/**
 * `SidebarSlider` — generic off-canvas slide-out shell for the right edge of
 * the dashboard. Owns the chrome (handle, header, close button, panel scroll
 * container, mobile slot positioning) and the cross-cutting mechanics
 * (collapsed-state localStorage persistence, header-offset CSS var, route +
 * role visibility gating, body-class side effect). Stays unaware of whatever
 * feature renders inside it.
 *
 * Two render slots:
 *   - `children` — desktop panel body. Caller passes a fragment containing
 *     whatever data view (list, status placeholder, etc.) the feature owns.
 *   - `mobileBar` — optional `<768px` content slot. The shell positions this
 *     element under the dashboard header (fixed bar) and hides it on desktop.
 *     Pass `null` to opt out of the mobile variant.
 *
 * The slider returns `null` (renders nothing) when the gate denies — so a
 * caller's data subscription, REST hydration, or any other side effects
 * inside `children` never run on excluded surfaces.
 *
 * @param {Object} props
 * @param {string} props.id - Unique identifier for this slider instance.
 *   Reserved for a future SidebarSliderStore (mutual exclusion across
 *   multiple sliders). Today it is unused except as a sanity prop name.
 * @param {string} [props.persistKey] - localStorage key for collapsed state.
 *   When absent, collapsed state is in-memory only and does not survive reload.
 * @param {'right'} [props.side='right'] - Edge to anchor on. Only `'right'`
 *   is implemented today; left-side support is a future addition.
 * @param {string} [props.handleLabel='Live'] - Text rendered vertically on
 *   the collapsed handle.
 * @param {string|React.ReactNode} [props.headerTitle] - Content in the
 *   panel's sticky header when expanded. Strings render inside an `<h3>`;
 *   React nodes render as-is so callers can put richer content (e.g. a tab
 *   bar) in the title slot. The close button sits to the right of this slot.
 * @param {string} [props.ariaLabel] - Explicit aria-label for the `<aside>`
 *   element. Falls back to `headerTitle` when it's a string, otherwise
 *   `'Sidebar'`. Required when `headerTitle` is a React node.
 * @param {string[]} [props.disabledRoutes=[]] - Path prefixes where the
 *   slider must not render (highest priority, overrides everything).
 * @param {string[]} [props.enabledRoutes=[]] - Path prefixes where the
 *   slider should render regardless of the default auth-route rule.
 * @param {string[]} [props.blockedRoles=[]] - Role names that hide the
 *   slider when the current user matches. Passed to
 *   `UserProfileService.isA()` which accepts string array of role names.
 * @param {string} [props.bodyClass] - CSS class applied to `<body>` while
 *   the slider is mounted. Caller-owned to avoid collision when multiple
 *   sliders coexist. Removed on unmount.
 * @param {'connected'|'connecting'|'degraded'|null} [props.pulseState] -
 *   Connection-state indicator rendered as a pulse dot on the handle and
 *   inside the panel header. Pass `null` (or omit) to suppress the dot.
 * @param {React.ReactNode} [props.mobileBar=null] - Optional content node
 *   rendered inside a fixed-position container on viewports `<768px`. The
 *   shell wraps it in a positioned `<div>`; the caller owns the inner
 *   markup.
 * @param {() => void} [props.onOpen] - Fired when the panel opens (after
 *   collapsed flips from true to false). Useful for hydration hooks the
 *   caller wants to defer until the user actually engages.
 * @param {() => void} [props.onClose] - Fired when the panel closes.
 * @param {React.ReactNode} props.children - Desktop panel body.
 */
function SidebarSlider(props) {
  // Gate FIRST, before any side-effect hooks. When the gate denies, this
  // function returns `null` and the inner component never mounts — so no
  // localStorage read, no body class added, no header-offset CSS var set, and
  // no caller side effects inside `children` ever execute.
  const { visible } = useSidebarGate({
    disabledRoutes: props.disabledRoutes,
    enabledRoutes: props.enabledRoutes,
    blockedRoles: props.blockedRoles,
  })
  if (!visible) return null
  return <SidebarSliderMounted {...props} />
}

function SidebarSliderMounted({
  id,
  persistKey,
  side = 'right',
  handleLabel = 'Live',
  headerTitle,
  ariaLabel,
  bodyClass,
  pulseState = null,
  mobileBar = null,
  onOpen,
  onClose,
  children,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    if (!persistKey) return true
    return localStorage.getItem(persistKey) !== 'false'
  })

  // Persist on flip. No-op when persistKey is absent.
  useEffect(() => {
    if (!persistKey) return
    localStorage.setItem(persistKey, String(collapsed))
  }, [persistKey, collapsed])

  // Fire open/close callbacks on transition. Skip the initial mount.
  const lastCollapsed = useRef(collapsed)
  useEffect(() => {
    if (collapsed === lastCollapsed.current) return
    lastCollapsed.current = collapsed
    if (collapsed) {
      onClose && onClose()
    } else {
      onOpen && onOpen()
    }
  }, [collapsed, onOpen, onClose])

  // Body class side effect — caller-owned class name, applied while mounted.
  useEffect(() => {
    if (!bodyClass) return
    document.body.classList.add(bodyClass)
    return () => document.body.classList.remove(bodyClass)
  }, [bodyClass])

  // Generic mounted-marker class so layout rules outside this component
  // (e.g., the dashboard's #page-content right-margin reservation for the
  // collapsed handle) can apply ONLY when a slider is actually present.
  // Hidden-via-role / hidden-via-route paths return null above, so the
  // class never lands for those users.
  useEffect(() => {
    document.body.classList.add('has-sidebar-slider')
    return () => document.body.classList.remove('has-sidebar-slider')
  }, [])

  // Keep --sidebar-slider-header-bottom in sync with the DashboardHeader's
  // measured height. ResizeObserver tracks the live header element so any
  // surface anchored to the var (this slider, plus the mobile slot) tracks
  // the header through reflows at narrower breakpoints.
  useHeaderOffset()

  const toggle = useCallback(() => setCollapsed(c => !c), [])

  const pulseClass = pulseState ? `SidebarSlider__pulse SidebarSlider__pulse--${pulseState}` : null

  return (
    <>
      <aside
        className={`SidebarSlider${collapsed ? ' is-collapsed' : ''}`}
        data-slider-id={id}
        data-slider-side={side}
        aria-label={ariaLabel || (typeof headerTitle === 'string' ? headerTitle : 'Sidebar')}
      >
        <button
          className='SidebarSlider__handle'
          onClick={toggle}
          aria-label={collapsed ? `Open ${handleLabel}` : `Close ${handleLabel}`}
          aria-expanded={!collapsed}
          type='button'
        >
          {pulseClass && <span className={pulseClass} aria-hidden='true' />}
          <span className='SidebarSlider__handle-label'>{handleLabel}</span>
        </button>
        {!collapsed && (
          <div className='SidebarSlider__panel'>
            <header className='SidebarSlider__header'>
              <div className='SidebarSlider__title'>
                {pulseClass && <span className={pulseClass} aria-hidden='true' />}
                {/* headerTitle accepts a string (renders as <h3>) or a React
                    node (rendered as-is). Caller-provided nodes take over the
                    title slot — used by `LiveLeaderboardSidebar` to put a tab
                    bar in the header row. */}
                {typeof headerTitle === 'string' ? headerTitle && <h3>{headerTitle}</h3> : headerTitle}
              </div>
              <button
                type='button'
                onClick={toggle}
                aria-label={`Close ${handleLabel}`}
                className='SidebarSlider__close'
              >
                <span aria-hidden='true'>×</span>
              </button>
            </header>
            {children}
          </div>
        )}
      </aside>
      {mobileBar && (
        <div className='SidebarSlider__mobile-slot' data-slider-id={id}>
          {mobileBar}
        </div>
      )}
    </>
  )
}

export default SidebarSlider
